import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from '../../design-system/toast';

import { useSettingsModal } from '../../contexts/SettingsModalContext';


type PaymenData = {
    amount: string;
    user_id: string;
    modal: "account";
    success: boolean;
    payment_type: "subscription" | "domain" | "quota_topup";
    section: "plan" | "domains" | "topups";
}

/**
 * Component that handles payment redirects with base64 encoded parameters
 * Captures /payment/:encodedData routes, decodes the data, shows notifications,
 * opens the appropriate settings modal, and redirects to the home page
 */
const PaymentRedirect: React.FC = () => {
  const { encodedData } = useParams<{ encodedData: string }>();
  const { openAccountSettings } = useSettingsModal();
  const navigate = useNavigate();

  useEffect(() => {
    if (encodedData) {
      try {
        const jsonString = atob(encodedData.replace(/-/g, '+').replace(/_/g, '/'));

        const paymentData = JSON.parse(jsonString) as PaymenData;

        const { success, payment_type, modal, section } = paymentData;

        const notificationMessages = {
            subscription: {
                success: 'Subscription payment successful! Your plan has been updated.',
                failure: 'Subscription payment failed. Please try again or contact support.'
            },
            domain: {
                success: 'Domain registration payment successful! Your domain has been registered.',
                failure: 'Domain registration payment failed. Please try again or contact support.'
            },
            quota_topup: {
                success: 'Quota top-up successful! Your plan limits have been updated.',
                failure: 'Quota top-up failed. Please try again or contact support.'
            },
        } as const;

        const message = notificationMessages[payment_type];

        if (success === true) {
            toast.success(message.success, { duration: 7000 });
        } else {
            toast.error(message.failure, { duration: 7000 });
        }

        // Open the appropriate modal section
        if (modal === 'account') {
          openAccountSettings(section);
        }
        
        navigate('/repositories', { replace: true });
      } catch (error) {
        console.error('Error processing payment redirect:', error);
      }
      navigate('/', { replace: true });
    }
  }, [encodedData, navigate, openAccountSettings]);

  return <div className="flex items-center justify-center min-h-screen">
    Processing payment...
  </div>;
};

export default PaymentRedirect;
